import './host'

import { GatewayService } from '@graft/nuget-gc.webportals.gateways'

import { getSessionToken } from '@common/helpers'
import { mapWithConcurrency } from '@common/utils/mapWithConcurrency'
import { readGraftObject } from '@common/utils/readGraftObject'

import type { Gateway, GatewayTimeFilterInput } from './gateways.interface'
import { hydrateGatewaysHostedModules } from './utils/hydrateGatewaysHostedModules'
import { invokeRemoveGateway } from './utils/invokeRemoveGateway'
import { resolveGatewayTimeFilterForApi } from './utils/resolveGatewayTimeFilter'

const GATEWAY_OBJECT_MAP_CONCURRENCY = 16

export {
  DEFAULT_GATEWAY_API_TIME_PRESET,
  DEFAULT_GATEWAY_UI_TIME_PRESET,
  GATEWAY_TIME_RANGE_PRESET_WINDOWS_MS,
} from './gateways.const'
export type { GatewayTimeFilterInput, ResolvedGatewayTimeFilter } from './gateways.interface'

export async function getGatewaysInDateRange(projectId: string, dateFrom: string, dateTo: string): Promise<Gateway[]> {
  if (!projectId) {
    return []
  }

  try {
    const token = await getSessionToken()
    const graftGateways = await GatewayService.getGateways(token, projectId, dateFrom, dateTo)

    const gateways = await mapWithConcurrency(
      graftGateways,
      (gateway) => readGraftObject<Gateway>(gateway),
      GATEWAY_OBJECT_MAP_CONCURRENCY
    )

    return hydrateGatewaysHostedModules(projectId, gateways)
  } catch (error) {
    console.error('Failed to get gateways:', error)
    return []
  }
}

export async function getGateways(projectId: string, filter?: GatewayTimeFilterInput): Promise<Gateway[]> {
  const { dateFrom, dateTo } = resolveGatewayTimeFilterForApi(filter)
  return getGatewaysInDateRange(projectId, dateFrom, dateTo)
}

export async function addNewGateway(projectId: string, name = ''): Promise<string | null> {
  try {
    const token = await getSessionToken()
    const gatewayId = await GatewayService.addNewGateway(token, projectId, name)
    return gatewayId ?? null
  } catch (error) {
    console.error('Failed to create gateway:', error)
    throw error
  }
}

export async function removeGateway(gatewayId: string): Promise<void> {
  if (!gatewayId) {
    throw new Error('Gateway id is required')
  }

  try {
    await invokeRemoveGateway(gatewayId)
  } catch (error) {
    console.error('Failed to remove gateway:', error)
    throw error
  }
}

const ugmInFlight = new Map<string, Promise<boolean>>()

export function didGatewayReportAnyUgm(gatewayKey: string): Promise<boolean> {
  const inflight = ugmInFlight.get(gatewayKey)
  if (inflight) return inflight

  const promise = (async () => {
    try {
      return await GatewayService.didGatewayReportAnyUgm(gatewayKey)
    } catch (error) {
      console.error('Failed to check if gateway reported any UGM:', error)
      return false
    } finally {
      ugmInFlight.delete(gatewayKey)
    }
  })()

  ugmInFlight.set(gatewayKey, promise)
  return promise
}
